import { inject, observer } from "mobx-react";
import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import ProfileSwitcherStore from "../../components/ProfileSwitcher/store";
import { UserSessionItemDto } from "../../components/ProfileSwitcher/types";
import { UserInfoDto } from "../../modules/MyProfile/types";
import { getActiveSessionKey, getActiveUser, isActiveAccountRoot } from "../../utils/SsoSession";
import { REDESIGN_NS } from "../i18n";
import { getAvatarColor, getInitials } from "../ui/avatar";
import { IconClose } from "../ui/icons";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  profile?: UserInfoDto | null;
  ProfileSwitcherStore?: ProfileSwitcherStore;
}

/**
 * Oturum satırının fotoğrafı; yoksa veya yüklenemezse baş harflere düşer.
 */
const SessionAvatar: FC<{ session: UserSessionItemDto }> = ({ session }) => {
  const [failed, setFailed] = useState(false);

  // Aynı satırda fotoğraf değişirse önceki hatayı taşımaz.
  useEffect(() => setFailed(false), [session.avatar]);

  const showImage = !!session.avatar && !failed;

  return (
    <span
      className="sso-rd__session-avatar"
      style={
        showImage ? undefined : { background: getAvatarColor(session.sessionKey) }
      }
    >
      {showImage ? (
        <img src={session.avatar} alt="" onError={() => setFailed(true)} />
      ) : (
        getInitials(session.name || session.userName)
      )}
    </span>
  );
};

const AccountMenu: FC<Props> = inject("ProfileSwitcherStore")(
  observer(({ isOpen, onClose, profile, ProfileSwitcherStore: store }: Props) => {
    const { t } = useTranslation(REDESIGN_NS);
    const history = useHistory();

    /*
     * Dışarı tıklama/Escape'i Header yönetir (`useDismissable`, avatar butonunu da kapsayan
     * anchor ile). Burada ikinci bir dinleyici kurulursa butona basmak "dışarı tıklama"
     * sayılıyor ve menü kapanıp hemen yeniden açılıyordu.
     */
    const close = useCallback(() => onClose(), [onClose]);

    const activeSessionKey = getActiveSessionKey();

    /*
     * Oturum listesi menünün açılmasını beklemez, kabuk yüklenirken çekilir: açılışa
     * bağlıyken istek yavaş döndüğünde liste sonradan beliriyor ve içerik zıplıyordu.
     * Tek hesaplı kullanıcıda `load()` hiç istek atmadan döner.
     */
    useEffect(() => {
      if (!store) return;
      if (!store.isLoaded || store.loadedFor !== activeSessionKey) {
        store.load();
      }
    }, [store, activeSessionKey]);

    const activeUser = getActiveUser();
    const current: UserSessionItemDto | undefined = store?.current;
    const isRoot = isActiveAccountRoot();

    const displayName = current?.name || profile?.name || activeUser?.username || "";
    const displayEmail = current?.email || profile?.email || activeUser?.email || "";
    const photo = current?.avatar || profile?.avatar;
    const sessions = store?.sessions || [];
    const hasProxySessions = sessions.some((s) => !s.isRoot);

    // Kapalıyken de DOM'da kalır: çıkış animasyonu ancak öyle oynayabilir.
    return (
      <div
        className={"sso-rd__account-menu" + (isOpen ? " is-open" : "")}
        role="menu"
      >
        {/* --- Kimlik başlığı --- */}
        <div className="sso-rd__account-identity">
          <div className="sso-rd__account-photo">
            {photo ? (
              <img src={photo} alt="" />
            ) : (
              <span>{getInitials(displayName)}</span>
            )}
          </div>
          <div className="sso-rd__account-name">{displayName}</div>
          {displayEmail && <div className="sso-rd__account-email">{displayEmail}</div>}
          <span
            className={
              "sso-rd__badge " +
              (isRoot ? "sso-rd__badge--main" : "sso-rd__badge--proxy")
            }
          >
            {isRoot ? t("Main Profile") : t("Proxy Login")}
          </span>
        </div>

        {/* --- Oturum listesi --- */}
        {sessions.length > 1 && (
          <div className="sso-rd__session-list">
            {sessions.map((session) => (
              <div key={session.sessionKey} className="sso-rd__session-item">
                <button
                  type="button"
                  role="menuitem"
                  className="sso-rd__session-row"
                  disabled={store?.isBusy || session.isCurrent}
                  onClick={() => store?.switchTo(session)}
                >
                  <SessionAvatar session={session} />
                  <span className="sso-rd__session-text">
                    <span className="sso-rd__session-name">
                      {session.name || session.userName}
                    </span>
                    <span className="sso-rd__session-username">{session.userName}</span>
                  </span>
                  <span
                    className={
                      "sso-rd__badge " +
                      (session.isRoot ? "sso-rd__badge--main" : "sso-rd__badge--proxy")
                    }
                  >
                    {session.isRoot ? t("Main Profile") : t("Proxy Login")}
                  </span>
                </button>
                {!session.isRoot && (
                  <button
                    type="button"
                    className="sso-rd__session-end"
                    title={t("End This Session")}
                    aria-label={t("End This Session")}
                    disabled={store?.isBusy}
                    onClick={() => store?.end(session)}
                  >
                    <IconClose size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* --- Aksiyonlar --- */}
        <div className="sso-rd__account-actions">
          <button
            type="button"
            className="sso-rd__account-action sso-rd__account-action--primary"
            onClick={() => {
              close();
              history.push("/my-profile");
            }}
          >
            {t("View My Profile")}
          </button>

          {hasProxySessions && (
            <button
              type="button"
              className="sso-rd__account-action sso-rd__account-action--secondary"
              disabled={store?.isBusy}
              onClick={() => store?.endOthers()}
            >
              {t("End Proxy Logins")}
            </button>
          )}

          <button
            type="button"
            className="sso-rd__account-action sso-rd__account-action--danger"
            disabled={store?.isBusy}
            onClick={() => store?.logout()}
          >
            {t("Safe Exit")}
          </button>
        </div>
      </div>
    );
  })
) as FC<Props>;

export default AccountMenu;
